// libraries
import React, { useState, useEffect } from "react";
import { filter, isEmpty, map, some, uniqBy } from "lodash";

// PropTypes
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Components
import { Dropdown } from "semantic-ui-react";
import IKModal from "@Components/IkModal";
import ConfirmModal from "@Modals/ConfirmModal";
import IkTable from "@Components/IkTable";

// Utils
import dataHandler from "@Utils/dataHandler";
import { dropdownFormat } from "@Utils/formats";

// Actions
import genericAction from "@Actions/generic";

// Styles
import styles from "./styles.module.css";

const ChatModalPlantilla = ({ onClose, channelsData, dispatch, isReadOnly, company }) => {
	const defaultParams = { page: 1, orderKey: "id", orderDir: "desc", rows: 15 };

	// states
	const [openConfirm, setOpenConfirm] = useState(false);
	const [channels, setChannels] = useState([]);
	const [selectedChannel, setSelectedChannel] = useState(null);
	const [templates, setTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(0);
	const [selectedItemTable, setSelectedItemTable] = useState([]);
	const [checkAllData, setCheckAllData] = useState([]);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [selectedGroup, setSelectedGroup] = useState(null);
	const [groupOptions, setGroupOptions] = useState([]);
	const [currentParams, setCurrentParams] = useState(defaultParams);
	const [lastParams, setLastParams] = useState({});
	const [currentPage, setCurrentPage] = useState(1);

	// vars
	const isGroups = some(company.companyFields, { type: "group" });
	const isFormValid = !isEmpty(selectedItemTable) && selectedTemplate;

	const loadAllGroups = async (channel) => {
		const allParams = {
			page: 1,
			rows: 99999,
			orderKey: "id",
			orderDir: "desc",
			"contact.channel": channel,
			fields: ["id", "contact.channel", "name", "phone", "email", "companyContactFields"],
		};

		const data = await dataHandler({
			dispatch,
			url: "contacts",
			params: allParams,
		});

		const dataItems = map(data.items, (items) => {
			return {
				...items,
				companyContactFields: filter(items.companyContactFields, { type: "group" }),
			};
		});

		const groupList = dataItems.flatMap((item) => {
			return item.companyContactFields
				.filter((field) => {
					return field.value && field.value.trim() !== "";
				})
				.map((field) => {
					return {
						key: field.value,
						text: field.value,
						value: field.value,
					};
				});
		});

		const groups = uniqBy(groupList, "value");
		setGroupOptions(groups);
	};

	useEffect(() => {
		init();
	}, [channelsData, dispatch]);

	useEffect(() => {
		if (selectedChannel) {
			setSelectedGroup(null);
			resetData();
			loadAllGroups(selectedChannel);

			const baseParams = buildParams(selectedChannel, null);
			const finalParams = isEmpty(lastParams) ? baseParams : { ...baseParams, ...lastParams };

			setCurrentParams(finalParams);
			if (selectedTemplate) {
				handlerData(finalParams, false, false, selectedTemplate);
			} else {
				handlerData(finalParams);
			}
		} else {
			resetData();
		}
	}, [selectedChannel]);

	useEffect(() => {
		if (!selectedChannel) return;

		if (currentPage !== 1 && (selectedTemplate || selectedGroup)) {
			setCurrentPage(1);
			return;
		}
		const baseParams = buildParams(selectedChannel, selectedGroup);
		const finalParams = { ...baseParams, ...lastParams, page: currentPage };

		setCurrentParams(finalParams);
		handlerData(finalParams, false, false, selectedTemplate);
	}, [selectedChannel, selectedTemplate, selectedGroup, currentPage]);

	useEffect(() => {
		if (selectedChannel) {
			const baseParams = buildParams(selectedChannel, selectedGroup);

			if (!selectedGroup && isEmpty(lastParams)) {
				resetData();
				const baseParamsNoFilter = buildParams(selectedChannel, null);
				setCurrentParams(baseParamsNoFilter);
				handlerData(baseParamsNoFilter);
				return;
			}

			if ((!selectedGroup && !lastParams) || (selectedGroup && isEmpty(lastParams))) {
				setLastParams({});
			}

			const finalParams = isEmpty(lastParams) ? baseParams : { ...baseParams, ...lastParams };
			setCurrentParams(finalParams);
			handlerData(finalParams);
		}
	}, [selectedGroup]);

	const init = async () => {
		setSelectedChannel(null);
		setSelectedTemplate(null);
		setLastParams({});

		if (channelsData?.length) {
			const whatsappPlatformNames = ["whatsapp", "personal", "whatsapp360"];
			const whatsappPlatformChannels = channelsData.filter((item) => {
				const platformName = item.platform?.name?.toLowerCase() || "";
				return whatsappPlatformNames.includes(platformName);
			});
			const mappedChannels = dropdownFormat(whatsappPlatformChannels, "label", "platform.name");
			setChannels(mappedChannels);
		}

		const templatePromise = dataHandler({
			dispatch,
			method: "GET",
			url: "/templates",
		});

		const [templateResponse] = await Promise.all([templatePromise]);
		const activeTemplates = filter(templateResponse.items || [], (tpl) => {
			return tpl.isActive;
		});
		const mappedTemplates = dropdownFormat(activeTemplates, "title", "id");
		setTemplates(mappedTemplates);
	};

	const resetData = () => {
		setFilteredItems([]);
		setFilteredCount(0);
		setSelectedItemTable([]);
		setSelectedGroup(null);
	};

	const buildParams = (channel, group) => {
		return {
			page: 1,
			rows: 15,
			orderKey: "id",
			orderDir: "desc",
			"contact.channel": channel,
			fields: ["id", "contact.channel", "name", "phone", "email", "companyContactFields"],
			...(group ? { companyContactFields: group } : {}),
		};
	};

	const handleCancel = () => {
		if (!selectedChannel || !selectedItemTable || !selectedTemplate) {
			onClose();
		} else {
			setOpenConfirm(true);
		}
	};

	const handleConfirmClose = () => {
		setOpenConfirm(false);
		onClose();
	};

	const onSuccess = () => {
		onClose();
	};

	const handlerSaved = async () => {
		const payload = {
			contactIds: selectedItemTable,
			templateId: selectedTemplate,
		};

		await dispatch(
			genericAction({
				method: "post",
				route: "templates/send",
				body: payload,
				onSuccess,
			})
		);
	};

	const handlerData = async (params, printable, findAll, templateIdParam) => {
		if (params.page) setCurrentPage(params.page);
		const finalParams = selectedGroup ? { ...currentParams, ...params } : { ...params };

		const searchParams = {};
		Object.keys(params).forEach((key) => {
			if (!["page", "rows", "orderKey", "orderDir", "contact.channel", "fields", "companyContactFields"].includes(key)) {
				if (params[key] && params[key].toString().trim() !== "") {
					searchParams[key] = params[key];
				}
			}
		});

		if (isEmpty(searchParams) && !selectedGroup && !isEmpty(lastParams)) {
			setLastParams({});
		} else if (!isEmpty(searchParams)) {
			setLastParams(searchParams);
		}

		const apiUrl = "contacts";
		const apiParams = {
			...finalParams,
			"contact.channel": selectedChannel,
			...(templateIdParam || selectedTemplate ? { templateId: templateIdParam || selectedTemplate } : {}),
		};

		const data = await dataHandler({
			dispatch,
			url: apiUrl,
			params: apiParams,
		});

		if (printable) {
			setFilteredPrintData(data.items);
		}
		if (findAll) {
			setCheckAllData(data.items);
		} else {
			parserItems(data);
		}
	};

	const parserItems = (array) => {
		const data = map(array.items, (items) => {
			const groupField = (items.companyContactFields || []).find((field) => {
				return field.type === "group";
			});
			return {
				...items,
				companyContactFields: filter(items.companyContactFields, { type: "group" }),
				grupo: groupField ? groupField.value : "",
			};
		});

		setFilteredCount(array.count);
		setFilteredItems(data);
	};

	const handlerMultiple = (selectedItems) => {
		const itemsselect = map(selectedItems, (item1) => {
			return item1.id;
		});
		setSelectedItemTable(itemsselect);
	};

	const changeChannel = async (platformName) => {
		setSelectedChannel(platformName);

		if (!isEmpty(platformName)) {
			const baseParams = buildParams(platformName, selectedGroup);
			const finalParams = isEmpty(lastParams) ? baseParams : { ...baseParams, ...lastParams };

			const contactPromise = dataHandler({
				dispatch,
				method: "GET",
				url: "contacts",
				params: {
					...finalParams,
					...(selectedTemplate ? { templateId: selectedTemplate } : {}),
				},
			});

			const [contactResponse] = await Promise.all([contactPromise]);

			parserItems(contactResponse);
		}
	};

	return (
		<>
			<IKModal
				title="Enviar Plantilla"
				onClose={handleCancel}
				onSuccess={handlerSaved}
				saveLabel="Enviar"
				saveDisabled={!isFormValid}
				cancelLabel="Salir"
				onCancel={handleCancel}
				size="large"
				fullHeight>
				<div className={styles.grid}>
					<div className={styles.containerfield}>
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Canal</legend>
							<Dropdown
								placeholder="Selecciona un canal"
								fluid
								selection
								options={channels}
								className={styles.dropdownclass}
								value={selectedChannel}
								onChange={(e, { value }) => {
									return changeChannel(value);
								}}
								disabled={isReadOnly}
								clearable
							/>
						</fieldset>
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Plantilla</legend>
							<Dropdown
								placeholder="Selecciona una plantilla"
								fluid
								selection
								clearable
								options={templates}
								className={styles.dropdownclass}
								value={selectedTemplate}
								onChange={(e, { value }) => {
									return setSelectedTemplate(value);
								}}
								disabled={isReadOnly || !selectedChannel}
							/>
						</fieldset>
					</div>

					<fieldset className={styles.fieldset}>
						<legend className={styles.legend}>Contacto</legend>
						<div className={styles.contactFieldContainer}>
							<IkTable
								data={filteredItems}
								columns={[
									{ multiple: true, key: "id", dataPosition: "center" },
									{ label: "ID", key: "id", format: "string", dataPosition: "center", width: 1 },
									{ label: "Plataforma", key: "contact.channel", format: "string", dataPosition: "center", width: 1 },
									{ label: "Nombre", key: "name", format: "string", width: 1 },
									{ label: "Teléfono", key: "phone", format: "phone", dataPosition: "center", width: 1 },
									{
										label: "Correo",
										key: "email",
										format: "string",
										headerPosition: "center",
										dataPosition: "center",
										width: !isGroups ? 15 : 1,
									},
									{
										label: "Grupo",
										key: "companyContactFields,value",
										format: "array",
										headerPosition: "left",
										dataPosition: "left",
										width: 15,
										canFilter: false,
										canSort: false,
										hidden: !isGroups,
									},
								]}
								customSearchKeys={[...(isGroups ? [{ label: "Grupo", key: "companyContactFields^" }] : [])]}
								totalRows={filteredCount}
								filteredPrintData={filteredPrintData}
								defaultParams={{ ...defaultParams, page: currentPage }}
								handlerMultiple={handlerMultiple}
								checkAllData={checkAllData}
								dataHandler={handlerData}
								searcher={{ width: "40rem", filterButton: false }}
								topButtons={
									isGroups
										? [
												{
													componentType: "DropDown",
													value: selectedGroup,
													options: groupOptions,
													onChange: (e, { value }) => {
														return setSelectedGroup(value);
													},
													disabled: !selectedChannel,
													clearable: true,
													placeholder: "Filtrar por grupo",
													fluid: true,
													className: styles.groupFilter,
													inputContainerStyle: styles.groupFilterContainer,
												},
										  ]
										: []
								}
							/>
						</div>
					</fieldset>
				</div>
			</IKModal>

			{openConfirm && (
				<ConfirmModal
					buttonLabel="Salir"
					visible
					header="Confirmar"
					fullContent="¿Deseas salir sin guardar los cambios?"
					onConfirm={handleConfirmClose}
					onDismiss={() => {
						return setOpenConfirm(false);
					}}
				/>
			)}
		</>
	);
};

ChatModalPlantilla.propTypes = {
	onClose: PropTypes.func,
	dispatch: PropTypes.func,
	channelsData: PropTypes.array,
	isReadOnly: PropTypes.bool,
	company: PropTypes.object,
};

function mapStateToProps({ company }) {
	return {
		channelsData: company.channels,
	};
}

export default connect(mapStateToProps)(ChatModalPlantilla);
